'use client';

import { UserViewModel } from '@hidayahlab/academy-suite-corelib';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, PhoneInput, useToast } from '@tajdid/tajdid-ui';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { checkUserExist } from '../../actions/auth-action';
import { phoneSchema, PhoneSchema } from '../schemas/phone.schema';

export type UserState = {
  isNewUser: boolean;
  user: Pick<UserViewModel, 'phone' | 'dialCode' | 'countryCode'>;
};

type PhoneProps = {
  handleStep: (step: number) => void;
  handleUserState: (userState: UserState) => void;
};

export default function Phone({ handleStep, handleUserState }: PhoneProps) {
  const { showToast } = useToast();
  const t = useTranslations();
  const [isPending, setIsPending] = useState(false);

  const phoneForm = useForm<PhoneSchema>({
    defaultValues: {
      phone: '',
      dialCode: '+880',
      countryCode: 'BD',
    },
    resolver: zodResolver(phoneSchema),
    mode: 'onChange',
  });

  return (
    <form
      onSubmit={phoneForm.handleSubmit(async input => {
        setIsPending(true);

        const response = await checkUserExist(input);

        if (response.status === 'error') {
          if (response.error.statusCode === 403) {
            showToast({
              title: 'লগইন সমস্যা',
              description: 'আপনি ইউজার অ্যাপের জন্য অনুমোদিত নন।',
              variant: 'error',
            });

            setIsPending(false);

            return;
          }

          showToast({
            title:
              response.error.error === 'OtpAlreadySentException'
                ? 'You have already requested verification code'
                : 'Error',
            description:
              response.error.error === 'OtpAlreadySentException'
                ? response.error.message
                : 'Unable to login, please try again later',
            variant: 'error',
          });
        }

        setIsPending(false);

        if (response.status === 'success') {
          handleStep(2);
          handleUserState({
            ...response.data,
            user: input,
          });
        }
      })}
    >
      <div className="container flex flex-col items-center w-full gap-8 md:max-w-md">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2">
            <h1 className="text-xl md:text-3xl font-bold text-[#333234]">
              {t('login.title')}
            </h1>
          </div>
          <p className="text-base text-gray-700">
            {t('login.start-learning-with-us')}
          </p>
        </div>
        <div className="w-full ">
          <PhoneInput
            {...phoneForm.register('phone')}
            placeholder={t('login.enter-phone-number')}
            label={t('login.phone-number-label')}
            error={
              phoneForm.formState.touchedFields.phone &&
              phoneForm.formState.errors.phone?.message
                ? phoneForm.formState.errors.phone?.message
                : undefined
            }
          />
          <input type="hidden" {...phoneForm.register('dialCode')} />
          <input type="hidden" {...phoneForm.register('countryCode')} />
        </div>
        <Button
          type="submit"
          fullWidth
          disabled={phoneForm.formState.isSubmitting}
          isLoading={isPending}
        >
          {t('login.next')}
        </Button>
      </div>
    </form>
  );
}
